import { ExecActState, TaskInfo, TaskStatus, taskShow, userGetTaskPrize, userJoinTask } from '@tencent/wphk-act-base-cgi';
import { CouponActInfoWithGoodsAndState, CouponActRelation, DistributionActivity, handleExecFail } from '@tencent/wphk-act-base-framework';
import { to } from '@tencent/wphk-base-utils';
import { compact, isEmpty } from 'lodash-es';
import { reactive } from 'vue';
import { COUPON_SORT_STATE, getFormattedExchActConfigs, getTaskName } from '../config';
export enum PayActType {
  'task',
  'exch'
}

export type PayActItem = {
  actType: PayActType,
  enActId: string,
  exchInfo: CouponActInfoWithGoodsAndState,
  taskInfo?: TaskInfo
};

let distributionActivities: DistributionActivity;
let exchActConfigs: Array<{ actType: PayActType, enActId: string }> = [];
/** 领奖来源 */
let disSource = '';
export const payAct = reactive({
  actList: [] as PayActItem[],
});

/** 初始化活动 */
export const initPayAct = async ({ taskName, signInActId, disSource: paramsDisSource }:
{ taskName?: string, signInActId?: string, disSource?: string }) => {
  if (!taskName || !signInActId) {
    return;
  }
  disSource = paramsDisSource || '';
  const tasks = await getTasks(getTaskName(taskName));
  exchActConfigs = getFormattedExchActConfigs(tasks.map(item => item.enActId), signInActId);
  // 查一共6个活动（5个任务+1个直接领）
  const exchActs = await getExchActs(exchActConfigs.map(item => item.enActId));
  Object.assign(payAct, { actList: mergeTaskAndExchActs({ tasks, exchActs }) });
};

/** 刷新活动 */
export const refreshPayAct = async ({ taskName }: { taskName?: string }) => {
  /** 重新查券&活动,重新查任務 */
  const [tasks, exchActs] = await Promise.all([
    getTasks(getTaskName(taskName)),
    reQryCoupon(),
  ]);
  Object.assign(payAct, { actList: mergeTaskAndExchActs({ tasks, exchActs }) });
};

const reQryCoupon = async () => {
  await distributionActivities.qryCoupon();
  return distributionActivities.couponList;
};

/** 执行领券 */
export const receiveCoupon = async (actItem: PayActItem) => {
  const { exchInfo, enActId, taskInfo } = actItem;
  if (actItem.actType === PayActType.exch) {
    const [err] = await to(distributionActivities.getCoupon({
      actListid: enActId,
      prizeId: exchInfo.prizeId,
      spId: disSource || exchInfo.spid,
    }));
    // await to 失败or成功都需要更新活动.失败屏蔽
    if (err) {
      throw err;
    }
  } else {
    const { userTaskid } = taskInfo!;
    const [err, res] = await to(userGetTaskPrize({ userTaskid, disSource }));
    // await 失败or成功都需要更新活动+任务。失败屏蔽
    if (err) {
      throw err;
    } else if (res && ExecActState.success !== res.execState) {
      handleExecFail(res.execState);
    }
  }
};

/** 用户报名任务 */
export const joinTask = async () => {
  const actItemList = payAct.actList;
  const taskIds = compact(getNotJoinTasks(actItemList).map(item => item.taskInfo?.taskId));
  if (isEmpty(taskIds)) {
    return { status: 'hasJoined' };
  }
  return await userJoinTask({ taskIdList: taskIds });
};

// 获取未报名的全部任务
export const getNotJoinTasks = (actItemList: PayActItem[]) => {
  const notJoinTasks = actItemList.filter(item => item.actType === PayActType.task
    && item.taskInfo?.status === TaskStatus.notReceiveTask);
  return notJoinTasks;
};

const mergeTaskAndExchActs = ({ tasks, exchActs }:
{ tasks: TaskInfo[], exchActs: CouponActInfoWithGoodsAndState[] }) => {
  // 保证exchAct的顺序,把5个任务拼起来
  const sortedExchList = exchActConfigs
    .filter(config => config.actType === PayActType.task)
    .map((enActConfig, index) => {
      const targetExchAct = exchActs.find(exch => exch.enActid === enActConfig.enActId)!;
      return { ...enActConfig, exchInfo: targetExchAct, taskInfo: tasks[index] };
    });
  // 第一个exchAct
  const firstExchConfig = exchActConfigs.find(item => item.actType === PayActType.exch)!;
  const firstExchAct: PayActItem = {
    ...firstExchConfig,
    exchInfo: exchActs.find(act => act.enActid === firstExchConfig.enActId)!,
  };
  return [firstExchAct].concat(sortedExchList);
};

const getTasks = async (taskNameId: string) => {
  const res = await taskShow({ actIdList: [taskNameId] });
  const taskList = res?.actTaskInfoList?.[0].basicTaskList;
  return sortTasksByRequireCount(taskList);
};

const getExchActs = async (actIds: string[]) => {
  if (!distributionActivities) {
    distributionActivities = new DistributionActivity({
      couponActRelation: CouponActRelation.oneForOne,
      distributeActidList: actIds,
      isNeedUserCouponid: true,
      isNeedCouponGoodsInfo: true,
      couponStateSort: COUPON_SORT_STATE,
    });
    await distributionActivities.initAct();
  }

  return distributionActivities.couponList;
};

const sortTasksByRequireCount = (tasks: TaskInfo[]) => tasks.sort((a, b) => a.requiredCount - b.requiredCount);
